import React, { useCallback, useRef, useState } from 'react';
import { Divider, ListItemIcon, Menu, MenuItem } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import TextureOutlinedIcon from '@mui/icons-material/TextureOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { FormattedMessage } from 'react-intl';

import { Button } from '../design-system';
import { FindBatchActionPopper } from './FindBatchActionPopper';
import { FindBatchDeleteDialog } from './FindBatchDeleteDialog';

export type FindBatchAction =
  | 'material'
  | 'type'
  | 'period'
  | 'date'
  | 'tags'
  | 'group'
  | 'site'
  | 'delete';
export type FindBatchUpdateAction = Exclude<FindBatchAction, 'delete'>;

interface FindBatchActionsMenuProps {
  findIds?: readonly string[];
  onAction?: (action: FindBatchAction) => void;
  onDeleted?: () => void;
}

const menuPaperStyles = {
  minWidth: 246,
  mt: 0.75,
  border: '1px solid',
  borderColor: 'softBorder',
  backgroundImage: 'none',
};
const deleteItemStyles = { color: 'error.main' };

const FindBatchActionsMenu = React.memo(
  ({ findIds = [], onAction, onDeleted }: FindBatchActionsMenuProps) => {
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const [
      activeAction,
      setActiveAction,
    ] = useState<FindBatchUpdateAction | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);
    const handleActionClose = useCallback(() => setActiveAction(null), []);
    const handleDeleteClose = useCallback(() => setDeleteOpen(false), []);
    const handleAction = useCallback(
      (action: FindBatchAction) => {
        onAction?.(action);
        setOpen(false);

        if (action === 'delete') {
          setActiveAction(null);
          setDeleteOpen(true);
        } else {
          setDeleteOpen(false);
          setActiveAction(action);
        }
      },
      [onAction]
    );
    const handleMaterial = useCallback(() => handleAction('material'), [
      handleAction,
    ]);
    const handleType = useCallback(() => handleAction('type'), [handleAction]);
    const handlePeriod = useCallback(() => handleAction('period'), [
      handleAction,
    ]);
    const handleDate = useCallback(() => handleAction('date'), [handleAction]);
    const handleTags = useCallback(() => handleAction('tags'), [handleAction]);
    const handleGroup = useCallback(() => handleAction('group'), [
      handleAction,
    ]);
    const handleSite = useCallback(() => handleAction('site'), [handleAction]);
    const handleDelete = useCallback(() => handleAction('delete'), [
      handleAction,
    ]);

    return (
      <>
        <Button
          ref={anchorRef}
          tone="neutral"
          endIcon={<KeyboardArrowDownIcon />}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={handleOpen}
        >
          <FormattedMessage
            id="finds.batch.title"
            defaultMessage="Bulk actions"
          />
        </Button>
        <Menu
          anchorEl={anchorRef.current}
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: menuPaperStyles }}
        >
          <MenuItem onClick={handleMaterial}>
            <ListItemIcon>
              <TextureOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.material"
              defaultMessage="Set material"
            />
          </MenuItem>
          <MenuItem onClick={handleType}>
            <ListItemIcon>
              <CategoryOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.type"
              defaultMessage="Set find type"
            />
          </MenuItem>
          <MenuItem onClick={handlePeriod}>
            <ListItemIcon>
              <TimelineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.period"
              defaultMessage="Set period"
            />
          </MenuItem>
          <MenuItem onClick={handleDate}>
            <ListItemIcon>
              <CalendarMonthOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.date"
              defaultMessage="Set find date"
            />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleTags}>
            <ListItemIcon>
              <LocalOfferOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage id="finds.batch.tags" defaultMessage="Add tags" />
          </MenuItem>
          <MenuItem onClick={handleGroup}>
            <ListItemIcon>
              <LayersOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.group"
              defaultMessage="Set group"
            />
          </MenuItem>
          <MenuItem onClick={handleSite}>
            <ListItemIcon>
              <LocationOnOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.site"
              defaultMessage="Change site"
            />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete} sx={deleteItemStyles}>
            <ListItemIcon sx={deleteItemStyles}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <FormattedMessage
              id="finds.batch.delete"
              defaultMessage="Delete selected"
            />
          </MenuItem>
        </Menu>
        {activeAction && (
          <FindBatchActionPopper
            key={activeAction}
            action={activeAction}
            anchorEl={anchorRef.current}
            findIds={findIds}
            onClose={handleActionClose}
          />
        )}
        {deleteOpen && (
          <FindBatchDeleteDialog
            findIds={findIds}
            onClose={handleDeleteClose}
            onDeleted={onDeleted}
          />
        )}
      </>
    );
  }
);

export { FindBatchActionsMenu };
